import React from 'react';
import { fromPairs, map, get, isEqual, find, size } from 'lodash';
import { button as Button } from 'modules/button';
import { Form } from 'modules/form';
import classnames from 'classnames';
import { popover } from 'modules/popover';
import { widgetError as WidgetError } from 'ui/views/widgets/widgetError';
import { WIDGET_ERROR_TYPE } from 'constants/common';

// local dependencies
import { getLabel } from 'utils/getLabel';
import { Icon } from 'modules/icon-bank';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { setPatientNotes as SetPatientNotes } from 'ui/views/widgets/setPatientNotes';
 *
 * // render
 * <SetPatientNotes propName='propValue'/>
 */
const AddPatientNotes = ( props ) => {

    const { actions = [] } = props;

    return (
        <Form
            fields={ fromPairs( map( props.fields, ( field ) => [ field.name, field ] ) ) } // convert to object as specified by `Form` prop
            values={ props.values.state.values } // default values
            isValid={ false }
            key={ props.formId }
            onSubmit={ ( { values, isValid } ) => {
                if( isValid ) {
                    props.onFormSubmit( {
                        ...values
                    } );
                }
            } }
        >
            { ( { fields, values, isValid, errors, onChange, onSumbit } = {} ) => {
                return (
                    <form
                        onSubmit={ onSumbit }
                        className='ui-set-patient-notes__body__form'
                    >
                        { /* fields */ }
                        { map( fields, ( field ) => {
                                    
                            const className = classnames(
                                'ui-set-patient-notes__body__form__field',
                                `ui-set-patient-notes__body__form__field--${ field.name }`
                            );

                            return (
                                <field.view
                                    className={ className }
                                    key={ field.name }
                                    name={ field.name }
                                    required={ field.required }
                                    config={ field.config }
                                    placeholder={ field.placeholder }
                                    label={ field.title }
                                    onChange={ onChange }
                                    value={ values[ field.name ] }
                                    error={ errors[ field.name ] }
                                    options={ get( props.values.options, field.options ) }
                                    mask={ field.mask }
                                    maxLength={ field.maxLength }
                                />
                            );
                            } ) }
                            {/* button actions */}
                            <div className='ui-set-patient-notes__body__actions'>
                                    { map( actions, action => {
                                        return (
                                            <Button
                                                { ...action }
                                                key={ action.name }
                                                id= { action.name }
                                                type= { 'cancel' !== action.name ? 'submit' : 'button' }
                                                disabled={ 'cancel' !== action.name ? !isValid : false }
                                                outline={ 'cancel' === action.name ? true: false }
                                                size='large'
                                                className='ui-set-patient-notes__body__actions__action'
                                                onClick={ isEqual( 'cancel', action.name ) ? () => {
                                                    popover.close();
                                                    props.setAdd( false );
                                                } : null }
                                            />
                                        );
                                    } ) }
                            </div>

                    </form>
                        );
                    } }
        </Form>
    );
};

const PatientNotes = ( props ) => {
    const { listActions = [] } = props;
    const patientNotes = props.values.state.patientNotes;
    return(
        <div className='ui-set-patient-notes__body__list'>
            <div className='ui-set-patient-notes__body__list__items'>

                { 0 === size( patientNotes ) && (
                    <WidgetError type={  WIDGET_ERROR_TYPE.NO_DATA_FOUND } />
                ) }

                { map( patientNotes, note => {
                    return (
                        <div className='ui-set-patient-notes__body__list__items__item' key={ note.date } >
                            <div className='ui-set-patient-notes__body__list__items__item__top' >
                                <div className='ui-set-patient-notes__body__list__items__item__top__type' >
                                    { note.type }
                                </div>
                                <div className='ui-set-patient-notes__body__list__items__item__top__date' >
                                    { getLabel( find( props.noteList, { name: 'date' } ).title ) } { note.date }
                                </div>
                            </div>
                            <div className='ui-set-patient-notes__body__list__items__item__center' >
                                <div className='ui-set-patient-notes__body__list__items__item__center__priority' >
                                    <div className='ui-set-patient-notes__body__list__items__item__center__priority__label' >
                                        { getLabel( find( props.noteList, { name: 'alertPriority' } ).title ) + ': '}
                                    </div>
                                    <div className='ui-set-patient-notes__body__list__items__item__center__priority__value' >
                                        { note.alertPriority }
                                    </div>
                                </div>
                                <div className='ui-set-patient-notes__body__list__items__item__center__originator' >
                                    <div className='ui-set-patient-notes__body__list__items__item__center__originator__label' >
                                        { getLabel( find( props.noteList, { name: 'originatorType' } ).title ) }
                                    </div>
                                    <div className='ui-set-patient-notes__body__list__items__item__center__originator__value' >
                                        { note.originatorType.name } { '(' + note.originatorType.position + ')' }
                                    </div>
                                </div>
                            </div>
                            <div className='ui-set-patient-notes__body__list__items__item__bottom' >
                                <div className='ui-set-patient-notes__body__list__items__item__bottom__description' >
                                    { note.description }
                                </div>
                            </div>
                            
                        </div>
                    );
                } ) }
            </div>
            <div className='ui-set-patient-notes__body__list__actions'>
                { map( listActions, action => {
                    return (
                        <Button
                            key={ action.name }
                            { ...action }
                            outline={ 'cancel' === action.name ? true: false }
                            size='large'
                            className='ui-set-patient-notes__body__list__actions__action'
                            onClick={ () => 'cancel' !== action.name ? props.setAdd( true ) : props.onCancelClick() }
                        />
                    );
                } ) }
            </div>
        </div>
    );
};

export const setPatientNotes = ( props ) => {
    
    const title = props.title ;

    const className = classnames( 'ui-set-patient-notes', {
        'ui-set-patient-notes--form': props.isAdd,
        'ui-set-patient-notes--list': ! props.isAdd
    } );

    return(
        <div className={ className }>
			<div className='ui-set-patient-notes__header'>

				{/* title for the modal */}
				<div className='ui-set-patient-notes__header__title'>
					{ getLabel( title ) }
				</div>

				{/* close button */}
                <div className='ui-error-overlay__head__close' onClick={ () => props.closeOverlay() }>
                    <Icon name='close' width='14' height='14' fill='#ffffff' />
                </div>

			</div>

            <div className='ui-set-patient-notes__body'>
                { ! props.isAdd ?
                    <PatientNotes { ...props } setAdd={ ( val ) => props.setAdd( val ) } />
                :
                    <AddPatientNotes { ...props } setAdd={ ( val ) => props.setAdd( val ) } />
                }
            </div>
           
        </div>
    );
};

// set display name
setPatientNotes.displayName = 'setPatientNotes';

// set default props
setPatientNotes.defaultProps = {};

// prop types
setPatientNotes.propTypes = {};
